"use client";
import { DragHandle } from "@tiptap-pro/extension-drag-handle-react";
import { Editor } from "@tiptap/react";
import { Icon } from "../../ui/Icon.js";
import { Toolbar } from "../../ui/Toolbar.js";

import * as Popover from "@radix-ui/react-popover";
import {
  Clipboard,
  Copy,
  GripVertical,
  Plus,
  RemoveFormatting,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { DropdownButton } from "../../ui/Dropdown/Dropdown.js";
import { Surface } from "../../ui/Surface.js";
import useContentItemActions from "./hooks/useContentItemActions.js";
import { useData } from "./hooks/useData.js";
import i18next from "i18next";

export type ContentItemMenuProps = {
  editor: Editor;
};

export const ContentItemMenu = ({ editor }: ContentItemMenuProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [tippyOptions, setTippyOptions] = useState();
  const data = useData();
  const actions = useContentItemActions(
    editor,
    data.currentNode,
    data.currentNodePos
  );

  useEffect(() => {
    if (menuOpen) {
      editor.commands.setMeta("lockDragHandle", true);
    } else {
      editor.commands.setMeta("lockDragHandle", false);
    }
  }, [editor, menuOpen]);

  return (
    <DragHandle
      pluginKey="ContentItemMenu"
      editor={editor}
      onNodeChange={data.handleNodeChange}
      tippyOptions={{
        placement: "left",
        offset: [-12, 2],
        zIndex: 99,
      }}
    >
      <div className="flex items-center gap-0.5">
        <Toolbar.Button type="button" onClick={actions.handleAdd}>
          <Icon icon={Plus} />
        </Toolbar.Button>
        <Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <Popover.Trigger asChild>
            <Toolbar.Button type="button">
              <Icon icon={GripVertical} />
            </Toolbar.Button>
          </Popover.Trigger>
          <Popover.Content side="bottom" align="start" sideOffset={8}>
            <Surface className="p-2 flex flex-col min-w-[16rem]">
              <Popover.Close>
                <DropdownButton onClick={actions.resetTextFormatting}>
                  <Icon icon={RemoveFormatting} />
                  {i18next.t("clearFormatting") || "Clear formatting"}
                </DropdownButton>
              </Popover.Close>
              <Popover.Close>
                <DropdownButton onClick={actions.copyNodeToClipboard}>
                  <Icon icon={Clipboard} />
                  {i18next.t("copyToClipboard") || "Copy to clipboard"}
                </DropdownButton>
              </Popover.Close>
              <Popover.Close>
                <DropdownButton onClick={actions.duplicateNode}>
                  <Icon icon={Copy} />
                  {i18next.t("duplicate") || "Duplicate"}
                </DropdownButton>
              </Popover.Close>
              <Toolbar.Divider horizontal />
              <Popover.Close>
                <DropdownButton
                  onClick={actions.deleteNode}
                  className="text-red-500 bg-red-500 dark:text-red-500 hover:bg-red-500 dark:hover:text-red-500 dark:hover:bg-red-500 bg-opacity-10 hover:bg-opacity-20 dark:hover:bg-opacity-20"
                >
                  <Icon icon={Trash2} />
                  {i18next.t("delete") || "Delete"}
                </DropdownButton>
              </Popover.Close>
            </Surface>
          </Popover.Content>
        </Popover.Root>
      </div>
    </DragHandle>
  );
};
